import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { NavLink, Outlet, Navigate, useLocation, Link } from "react-router-dom";
import { Shield, LayoutGrid, Users, Users2, Database, ScrollText, Lock, SlidersHorizontal, FileStack, AtSign, Flag, Tag, KeyRound, BarChart3, HardDrive, ShieldAlert, Mail, MailCheck, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useRealtime } from "@/context/RealtimeContext";
import { EmptyState } from "@/components/common/Loading";
import { Button } from "@/components/ui/button";

const MODERATION_TAB = { to: "/dashboard/admin/moderation", label: "Moderation", icon: Flag };
// Phase 9 — appears only when the Otter Assist admin console is switched on for this deployment.
const OTTER_ASSIST_TAB = { to: "/dashboard/admin/otter-assist", label: "Otter Assist", icon: Sparkles };

const ADMIN_TABS = [
  { to: "/dashboard/admin", label: "Overview", icon: LayoutGrid, end: true },
  { to: "/dashboard/admin/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/dashboard/admin/users", label: "Users & Tiers", icon: Users },
  { to: "/dashboard/admin/reference", label: "Reference Data", icon: Database },
  { to: "/dashboard/admin/handles", label: "Handles", icon: AtSign },
  { to: "/dashboard/admin/entitlements", label: "Entitlements", icon: SlidersHorizontal },
  { to: "/dashboard/admin/pricing", label: "Pricing", icon: Tag },
  { to: "/dashboard/admin/licenses", label: "Licenses", icon: KeyRound },
  { to: "/dashboard/admin/content", label: "Content", icon: FileStack },
  { to: "/dashboard/admin/email-templates", label: "Email Templates", icon: Mail },
  { to: "/dashboard/admin/storage", label: "Storage", icon: HardDrive },
  MODERATION_TAB,
  { to: "/dashboard/admin/groups", label: "Groups", icon: Users2 },
  { to: "/dashboard/admin/audit", label: "Audit Log", icon: ScrollText },
  { to: "/dashboard/admin/security", label: "Security", icon: ShieldAlert },
  { to: "/dashboard/admin/consent-log", label: "Consent Log", icon: MailCheck },
];

export default function AdminHome() {
  const { user } = useAuth();
  const { sectionCounts = {} } = useRealtime();
  const location = useLocation();
  // The console flag is deployment-controlled and the endpoint 404s while it is off, so an absent
  // tab is indistinguishable from a deployment that never had it.
  const { data: oaAdmin } = useQuery({
    queryKey: ["oa-admin-me"],
    queryFn: async () => (await api.get("/admin/otter-assist/me")).data,
    retry: false,
  });
  const otterAssistEnabled = !!oaAdmin?.level;
  const modCount = sectionCounts.moderation || 0;
  const role = user?.role;
  const isAdmin = role === "admin";
  const isModerator = role === "moderator";

  if (user && !isAdmin && !isModerator) {
    return <EmptyState icon={Lock} title="Admins only" description="You don't have permission to view the Admin Console." testId="admin-forbidden" />;
  }

  // Moderators only get the Moderation queue — send them there from the console root.
  if (isModerator && !isAdmin && location.pathname === "/dashboard/admin") {
    return <Navigate to="/dashboard/admin/moderation" replace />;
  }

  // MFA enforcement: an admin-capable user without 2FA is blocked from admin
  // tools once MFA_ADMIN_ENFORCED is on (server also enforces this on every
  // admin API route). While enforcement is off we show a soft nudge instead.
  const mfaCapable = isAdmin || isModerator;
  const mfaEnabled = !!user?.mfa_enabled;
  const mfaEnforced = !!user?.mfa_enforced;
  if (mfaCapable && !mfaEnabled && mfaEnforced) {
    return (
      <div data-testid="admin-mfa-required">
        <EmptyState icon={ShieldAlert} title="Set up two-factor authentication to continue"
          description="Your role requires 2FA to access admin tools. Enable it in Account Settings, then come back." />
        <div className="mt-4 flex justify-center">
          <Button asChild data-testid="admin-mfa-setup-link"><Link to="/dashboard/account">Go to Account Settings</Link></Button>
        </div>
      </div>
    );
  }

  const base = isAdmin ? ADMIN_TABS : [MODERATION_TAB];
  const tabs = otterAssistEnabled ? [...base, OTTER_ASSIST_TAB] : base;
  const title = isAdmin ? "Admin Console" : "Moderation";
  const subtitle = isAdmin
    ? "Manage members, reference data and platform activity."
    : "Review reported content and keep Chatter safe.";

  return (
    <div data-testid="admin-page">
      <div className="flex items-center gap-2.5">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-teal-50 text-primary">
          {isAdmin ? <Shield className="h-5 w-5" /> : <Flag className="h-5 w-5" />}
        </span>
        <div>
          <h1 className="text-2xl font-[680] tracking-[-0.01em] text-foreground">{title}</h1>
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </div>
      </div>

      {mfaCapable && !mfaEnabled && !mfaEnforced && (
        <div className="mt-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid="admin-mfa-nudge">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Two-factor authentication will soon be required for admin access. <Link to="/dashboard/account" className="font-[560] underline">Set it up now</Link> to avoid interruption.</span>
        </div>
      )}

      <div className="mt-5 flex flex-wrap gap-1.5 border-b border-border pb-px">
        {tabs.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.end}
            data-testid={`admin-tab-${t.label.toLowerCase().split(" ")[0]}`}
            className={({ isActive }) =>
              `inline-flex items-center gap-2 rounded-t-lg px-3.5 py-2 text-sm font-[560] transition-colors ${
                isActive ? "bg-teal-50 text-teal-700" : "text-foreground/60 hover:bg-secondary hover:text-foreground"
              }`
            }
          >
            <t.icon className="h-4 w-4" /> {t.label}
            {t.to === MODERATION_TAB.to && modCount > 0 && (
              <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-[640] text-primary-foreground" data-testid="admin-tab-moderation-badge">{modCount > 9 ? "9+" : modCount}</span>
            )}
          </NavLink>
        ))}
      </div>

      <div className="mt-6">
        <Outlet />
      </div>
    </div>
  );
}
